import { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { UserPlus, Trash2, Pencil, X, Users, Shield, Star, Check, Search, KeyRound, Phone, Camera, Loader2 } from "lucide-react";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * إنشاء حسابات إدارية متعددة — master-only screen.
 * The owner (07811557910) can:
 *   • create sub-admin accounts (name, phone, password)
 *   • pick which admin tabs each sub-admin is allowed to see (star = allowed)
 *   • edit permissions / rename / reset password / delete
 * Sub-admins log in with the same /auth/login and receive `allowed_tabs`
 * on their user payload; Admin.jsx uses this list to filter ADMIN_TILES
 * so they only ever see what the master granted.
 * Requested Feb 27, 2026.
 */
const SubAdminsTab = ({ availableTiles = [], tileGroups = [], homeWidgets = [], portalIcons = [], onOpenPermissions }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // sub-admin doc or null
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/sub-admins`);
      setList(data.sub_admins || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التحميل");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((u) =>
      (u.full_name || "").toLowerCase().includes(s) ||
      (u.phone || "").toLowerCase().includes(s)
    );
  }, [list, q]);

  const remove = async (u) => {
    const ok = await confirmDialog(
      `حذف الحساب "${u.full_name}" نهائياً؟\n\nراح يفقد هذا الحساب الوصول للنظام.`,
      { force: true, tone: "danger", title: "تأكيد الحذف", confirmLabel: "احذف", cancelLabel: "إلغاء" }
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/sub-admins/${u.id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحذف");
    }
  };

  return (
    <div className="space-y-4" data-testid="sub-admins-tab" dir="rtl">
      <div className="bg-gradient-to-l from-[#7C3AED] via-[#2563EB] to-[#0EA5E9] text-white rounded-2xl p-4 shadow-md">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Users className="w-6 h-6" />
            <div>
              <h3 className="font-black text-lg">حسابات إدارية متعددة</h3>
              <p className="text-[11px] opacity-90">أنشئ حسابات ثانوية لموظفينك مع صلاحيات محدودة</p>
            </div>
          </div>
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            data-testid="sub-admin-create-btn"
            className="bg-white text-[#2563EB] hover:bg-white/90 font-black px-4 py-2 rounded-full flex items-center gap-2 shadow-lg active:scale-95 transition-transform"
          >
            <UserPlus className="w-4 h-4" />
            إنشاء حساب جديد
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="ابحث بالاسم أو الرقم..."
          data-testid="sub-admins-search"
          className="w-full pr-9 pl-4 py-2.5 rounded-full bg-white border border-[#E4EBE6] text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]"
        />
      </div>

      {/* List */}
      {loading ? (
        <p className="text-center py-12 text-[#5C6E64]">جاري التحميل...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-[#E4EBE6]">
          <Shield className="w-12 h-12 mx-auto mb-2 text-[#5C6E64] opacity-50" />
          <p className="text-[#5C6E64] font-medium">
            {q ? "لا توجد نتائج" : "لا توجد حسابات ثانوية بعد — اضغط \"إنشاء حساب جديد\""}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((u) => (
            <motion.div
              key={u.id}
              layout
              data-testid={`sub-admin-card-${u.id}`}
              className="bg-white border-2 border-[#7C3AED]/30 rounded-2xl p-3.5 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#2563EB] text-white flex items-center justify-center font-black text-lg shrink-0">
                    {(u.full_name || "?")[0]}
                  </div>
                  <div className="min-w-0">
                    <p className="font-black text-[#1E3A5F] truncate">{u.full_name}</p>
                    <p className="text-[11px] text-[#F5A623] font-bold" dir="ltr">{u.phone}</p>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => { setEditing(u); setShowForm(true); }}
                    data-testid={`sub-admin-edit-${u.id}`}
                    className="bg-[#3B82F6] hover:bg-[#2563EB] text-white p-1.5 rounded-full transition-colors"
                    title="تعديل"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => remove(u)}
                    data-testid={`sub-admin-delete-${u.id}`}
                    className="bg-[#E85D45] hover:bg-[#C24A35] text-white p-1.5 rounded-full transition-colors"
                    title="حذف"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-[11px] text-[#5C6E64]">
                <Star className="w-3.5 h-3.5 text-[#F5A623] fill-[#F5A623]" />
                <span className="font-bold" dir="ltr">{(u.allowed_tabs || []).length}</span>
                <span>تبويب مسموح</span>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {showForm && (
        <SubAdminFormModal
          editing={editing}
          availableTiles={availableTiles}
          tileGroups={tileGroups}
          homeWidgets={homeWidgets}
          portalIcons={portalIcons}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

// ─── Create / Edit modal ───────────────────────────────────────────────────
const SubAdminFormModal = ({ editing, availableTiles, tileGroups, homeWidgets, portalIcons, onClose, onSaved }) => {
  const isEdit = !!editing;
  const photoRef = useRef(null);
  const [name, setName] = useState(editing?.full_name || "");
  const [phone, setPhone] = useState(editing?.phone || "");
  const [password, setPassword] = useState("");
  const [profilePhoto, setProfilePhoto] = useState(editing?.profile_photo || "");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [welcomeText, setWelcomeText] = useState(editing?.welcome_text || "");
  const [welcomeAudio, setWelcomeAudio] = useState(editing?.welcome_audio || "");
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const audioRef = useRef(null);
  const previewAudioRef = useRef(null);
  const [allowed, setAllowed] = useState(() => new Set(editing?.allowed_tabs || []));
  const [busy, setBusy] = useState(false);
  const [tabQ, setTabQ] = useState("");

  const uploadAudio = async (file) => {
    if (!file) return;
    setUploadingAudio(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload-welcome-audio`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setWelcomeAudio(data.path);
      toast.success("تم رفع الصوت");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل رفع الصوت");
    } finally { setUploadingAudio(false); }
  };

  const previewAudio = () => {
    if (!welcomeAudio) return;
    if (previewAudioRef.current) {
      previewAudioRef.current.currentTime = 0;
      previewAudioRef.current.play();
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload-profile-photo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfilePhoto(data.path);
      toast.success("تم رفع الصورة");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل رفع الصورة");
    } finally { setUploadingPhoto(false); }
  };

  const toggle = (id) => {
    setAllowed((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const filteredTiles = useMemo(() => {
    const s = tabQ.trim().toLowerCase();
    if (!s) return availableTiles;
    return availableTiles.filter((t) => (t.label || "").toLowerCase().includes(s));
  }, [availableTiles, tabQ]);

  const grouped = useMemo(() => {
    const g = {};
    for (const t of filteredTiles) {
      const parent = tileGroups.find((tg) => tg.children.includes(t.id));
      const key = parent?.label || "أخرى";
      if (!g[key]) g[key] = [];
      g[key].push(t);
    }
    return g;
  }, [filteredTiles, tileGroups]);

  const selectAllVisible = () => {
    setAllowed((prev) => {
      const n = new Set(prev);
      for (const t of filteredTiles) n.add(t.id);
      return n;
    });
  };
  const clearAllVisible = () => {
    setAllowed((prev) => {
      const n = new Set(prev);
      for (const t of filteredTiles) n.delete(t.id);
      return n;
    });
  };

  const save = async () => {
    if (!name.trim()) { toast.error("الاسم مطلوب"); return; }
    if (!isEdit) {
      if (!/^\d{11}$/.test(phone.trim())) { toast.error("رقم الهاتف يجب 11 رقم"); return; }
      if (!password || password.length < 4) { toast.error("كلمة السر يجب 4 أحرف على الأقل"); return; }
    }
    setBusy(true);
    try {
      if (isEdit) {
        const body = { full_name: name.trim(), allowed_tabs: Array.from(allowed), profile_photo: profilePhoto, welcome_text: welcomeText, welcome_audio: welcomeAudio };
        if (password) body.password = password;
        await axios.patch(`${API}/admin/sub-admins/${editing.id}`, body);
        toast.success("تم حفظ التعديل");
      } else {
        await axios.post(`${API}/admin/sub-admins`, {
          full_name: name.trim(),
          phone: phone.trim(),
          password,
          allowed_tabs: Array.from(allowed),
          profile_photo: profilePhoto,
          welcome_text: welcomeText,
          welcome_audio: welcomeAudio,
        });
        toast.success("✅ تم إنشاء الحساب");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[300] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3"
        onClick={onClose}
        data-testid="sub-admin-form-overlay"
      >
        <motion.div
          initial={{ scale: 0.9, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          onClick={(e) => e.stopPropagation()}
          data-testid="sub-admin-form-modal"
          className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col"
          dir="rtl"
        >
          {/* Header */}
          <div className="bg-gradient-to-l from-[#7C3AED] to-[#2563EB] text-white px-4 py-3 flex items-center justify-between">
            <h3 className="font-black text-base flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              {isEdit ? "تعديل الحساب" : "إنشاء حساب إداري ثانوي"}
            </h3>
            <button onClick={onClose} className="bg-white/20 hover:bg-white/30 p-1.5 rounded-full transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Profile photo (small, deletable) */}
            <div className="bg-[#F0F9FF] rounded-2xl border-2 border-[#3B82F6]/30 p-3 flex items-center gap-3">
              <div className="relative shrink-0">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#2563EB] text-white flex items-center justify-center font-black text-2xl overflow-hidden shadow border-2 border-white">
                  {profilePhoto ? (
                    <img src={`${API}/files/${profilePhoto}`} alt="photo" className="w-full h-full object-cover" />
                  ) : (
                    (name || "?")[0]
                  )}
                  {uploadingPhoto && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <Loader2 className="w-5 h-5 animate-spin text-white" />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => photoRef.current?.click()}
                  data-testid="sub-admin-photo-upload"
                  className="absolute bottom-0 right-0 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-full p-1 shadow border-2 border-white"
                  title="رفع صورة"
                >
                  <Camera className="w-3 h-3" />
                </button>
                {profilePhoto && (
                  <button
                    type="button"
                    onClick={() => setProfilePhoto("")}
                    data-testid="sub-admin-photo-remove"
                    className="absolute bottom-0 left-0 bg-[#E85D45] hover:bg-[#C24A35] text-white rounded-full p-1 shadow border-2 border-white"
                    title="حذف الصورة"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
                <input
                  ref={photoRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => uploadPhoto(e.target.files?.[0])}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-black text-[#1E3A5F]">صورة البروفايل (اختياري)</p>
                <p className="text-[10px] text-[#5C6E64] mt-0.5">
                  {profilePhoto ? "الصورة مرفوعة — اضغط الحذف الأحمر لإزالتها" : "اضغط الكاميرا لرفع صورة الحساب"}
                </p>
              </div>
            </div>

            {/* Basic info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">الاسم الكامل *</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="sub-admin-name-input"
                  className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#7C3AED] text-sm"
                  placeholder="اسم الموظف"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">
                  <Phone className="inline w-3.5 h-3.5 ml-1" />
                  رقم الهاتف *
                </label>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 11))}
                  disabled={isEdit}
                  maxLength={11}
                  inputMode="numeric"
                  data-testid="sub-admin-phone-input"
                  className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#7C3AED] text-sm disabled:bg-[#F3F6F4] disabled:text-[#5C6E64]"
                  placeholder="07XXXXXXXXX"
                />
                {isEdit && <p className="text-[10px] text-[#5C6E64] mt-1">رقم الهاتف ثابت — لا يتغير</p>}
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">
                  <KeyRound className="inline w-3.5 h-3.5 ml-1" />
                  كلمة السر {isEdit ? "(اتركها فارغة إذا ما تريد تغييرها)" : "*"}
                </label>
                <input
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="sub-admin-password-input"
                  className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#7C3AED] text-sm"
                  placeholder={isEdit ? "•••• (لا تغيير)" : "4 أحرف على الأقل"}
                />
              </div>
            </div>

            {/* Welcome splash config — text + audio played once when this
                sub-admin logs into the admin panel. Requested Feb 27, 2026
                (TikTok-style splash screen). */}
            <div className="bg-gradient-to-l from-[#EDE9FE] via-[#FBCFE8] to-[#FED7AA] rounded-2xl border-2 border-[#7C3AED] p-3 space-y-2.5">
              <div className="flex items-center gap-2">
                <span className="text-lg">🎤</span>
                <h4 className="font-black text-sm text-[#4C1D95]">شاشة الترحيب (اختياري)</h4>
              </div>
              <p className="text-[10px] text-[#4C1D95]/80 leading-snug">
                تظهر أول مرة يدخل بها هذا الحساب للإدارة — بصورته وترحيب مخصص وصوت اختياري.
              </p>
              <div>
                <label className="text-[11px] font-bold text-[#4C1D95] mb-1 block">نص الترحيب</label>
                <input
                  value={welcomeText}
                  onChange={(e) => setWelcomeText(e.target.value)}
                  data-testid="sub-admin-welcome-text"
                  maxLength={120}
                  className="w-full px-3 py-2 rounded-xl border border-[#7C3AED]/40 focus:outline-none focus:ring-2 focus:ring-[#7C3AED] text-sm bg-white"
                  placeholder="مثال: مرحباً بك في تطبيق جملتي"
                />
                <p className="text-[9px] text-[#5C6E64] text-left mt-0.5" dir="ltr">{welcomeText.length}/120</p>
              </div>
              <div>
                <label className="text-[11px] font-bold text-[#4C1D95] mb-1 block">ملف الصوت</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => audioRef.current?.click()}
                    disabled={uploadingAudio}
                    data-testid="sub-admin-welcome-audio-upload"
                    className="flex-1 bg-white hover:bg-[#F5F3FF] border-2 border-[#7C3AED] text-[#4C1D95] font-black text-xs px-3 py-2 rounded-xl flex items-center justify-center gap-1.5 disabled:opacity-60"
                  >
                    {uploadingAudio ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span>🎵</span>}
                    {welcomeAudio ? "تغيير الصوت" : "رفع صوت"}
                  </button>
                  {welcomeAudio && (
                    <>
                      <button
                        type="button"
                        onClick={previewAudio}
                        data-testid="sub-admin-welcome-audio-play"
                        className="bg-[#15803D] hover:bg-[#126B32] text-white text-xs font-bold px-3 py-2 rounded-xl"
                        title="استماع"
                      >
                        ▶️
                      </button>
                      <button
                        type="button"
                        onClick={() => setWelcomeAudio("")}
                        data-testid="sub-admin-welcome-audio-remove"
                        className="bg-[#E85D45] hover:bg-[#C24A35] text-white text-xs font-bold p-2 rounded-xl"
                        title="حذف الصوت"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
                <input
                  ref={audioRef}
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={(e) => uploadAudio(e.target.files?.[0])}
                />
                {welcomeAudio && (
                  <audio ref={previewAudioRef} src={`${API}/files/${welcomeAudio}`} preload="none" />
                )}
                <p className="text-[9px] text-[#5C6E64] mt-1">MP3/WAV/M4A — حد أقصى 5MB</p>
              </div>
            </div>

            {/* Portal-view icons — the colored square buttons at the top-right
                of the admin panel (view as Customer / Driver / Mujahez / Rep /
                Complaints). Sub-admins only see the portals the master granted. */}
            {portalIcons.length > 0 && (
              <div className="bg-gradient-to-l from-[#FEE2E2] via-[#FEF3C7] to-[#DBEAFE] rounded-2xl border-2 border-[#F59E0B] p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-5 h-5 text-[#B45309] fill-[#F59E0B]" />
                  <h4 className="font-black text-sm text-[#78350F]">أيقونات التنقل بين البورتالات</h4>
                </div>
                <p className="text-[10px] text-[#78350F]/90 mb-2 leading-snug">
                  الأيقونات المربعة الملونة فوق لوحة الإدارة (كزبون / كسائق / كمجهّز / كمشرف / الشكاوي).
                  دوس على أي أيقونة لتشعّلها أو تطفيها لهذا الحساب.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                  {portalIcons.map((p) => {
                    const on = allowed.has(p.id);
                    return (
                      <button
                        type="button"
                        key={p.id}
                        onClick={() => toggle(p.id)}
                        data-testid={`portal-toggle-${p.id}`}
                        style={on ? { borderColor: p.color } : {}}
                        className={`relative text-right rounded-xl px-3 py-2.5 text-[12px] font-bold border-2 transition-all flex items-center gap-2 ${
                          on
                            ? "bg-white text-[#111814] shadow"
                            : "bg-[#F3F6F4] border-[#E4EBE6] text-[#5C6E64] opacity-70"
                        }`}
                      >
                        <span
                          className="inline-block w-6 h-6 rounded-lg shrink-0"
                          style={{ backgroundColor: on ? p.color : "#D1D5DB" }}
                        />
                        <span className="truncate">{p.label}</span>
                        {on && (
                          <Check className="w-3.5 h-3.5 mr-auto shrink-0" style={{ color: p.color }} strokeWidth={3} />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Permissions grid */}
            <div className="bg-[#FEF3C7] rounded-2xl border-2 border-[#F59E0B] p-3">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-[#F59E0B] fill-[#F59E0B]" />
                  <h4 className="font-black text-sm text-[#78350F]">
                    الصلاحيات — <span dir="ltr">{allowed.size}</span> مسموح
                  </h4>
                </div>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={selectAllVisible}
                    data-testid="perms-select-all"
                    className="bg-[#15803D] hover:bg-[#126B32] text-white text-[11px] font-bold px-2.5 py-1 rounded-full"
                  >
                    اختر الكل
                  </button>
                  <button
                    type="button"
                    onClick={clearAllVisible}
                    data-testid="perms-clear-all"
                    className="bg-[#5C6E64] hover:bg-[#3a4a40] text-white text-[11px] font-bold px-2.5 py-1 rounded-full"
                  >
                    امسح الكل
                  </button>
                </div>
              </div>
              <div className="relative mb-2">
                <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#78350F]/70" />
                <input
                  value={tabQ}
                  onChange={(e) => setTabQ(e.target.value)}
                  placeholder="ابحث عن تبويب..."
                  data-testid="perms-search"
                  className="w-full pr-8 pl-3 py-1.5 rounded-full bg-white/80 border border-[#F59E0B]/40 text-xs focus:outline-none"
                />
              </div>
              <div className="space-y-3 max-h-[46vh] overflow-y-auto">
                {Object.entries(grouped).map(([groupName, items]) => (
                  <div key={groupName}>
                    <p className="text-[11px] font-bold text-[#78350F] mb-1 pb-1 border-b border-[#F59E0B]/30">
                      📂 {groupName} ({items.length})
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                      {items.map((t) => {
                        const on = allowed.has(t.id);
                        return (
                          <button
                            type="button"
                            key={t.id}
                            onClick={() => toggle(t.id)}
                            data-testid={`perm-tile-${t.id}`}
                            data-on={on ? "1" : "0"}
                            className={`relative text-right rounded-xl px-2.5 py-2 text-[11px] font-bold border-2 transition-all ${
                              on
                                ? "bg-gradient-to-br from-[#FEF3C7] to-[#FBBF24] border-[#F59E0B] text-[#78350F] shadow"
                                : "bg-white border-[#E4EBE6] text-[#5C6E64] hover:border-[#F59E0B]/50"
                            }`}
                          >
                            {on && (
                              <Check className="absolute top-1 left-1 w-3 h-3 text-[#78350F]" strokeWidth={3} />
                            )}
                            <div className="flex items-center gap-1.5">
                              {on && <Star className="w-3 h-3 fill-[#F59E0B] text-[#F59E0B] shrink-0" />}
                              <span className="truncate">{t.label}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Home dashboard widgets picker */}
            {homeWidgets.length > 0 && (
              <div className="bg-[#EFF6FF] rounded-2xl border-2 border-[#3B82F6]/40 p-3">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-[#3B82F6] fill-[#3B82F6]" />
                    <h4 className="font-black text-sm text-[#1E3A5F]">مربعات الرئيسية (الهوم)</h4>
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => setAllowed((prev) => { const n = new Set(prev); homeWidgets.forEach((w) => n.add(w.id)); return n; })}
                      data-testid="home-widgets-select-all"
                      className="bg-[#15803D] hover:bg-[#126B32] text-white text-[11px] font-bold px-2.5 py-1 rounded-full"
                    >
                      اختر الكل
                    </button>
                    <button
                      type="button"
                      onClick={() => setAllowed((prev) => { const n = new Set(prev); homeWidgets.forEach((w) => n.delete(w.id)); return n; })}
                      data-testid="home-widgets-clear-all"
                      className="bg-[#5C6E64] hover:bg-[#3a4a40] text-white text-[11px] font-bold px-2.5 py-1 rounded-full"
                    >
                      امسح الكل
                    </button>
                  </div>
                </div>
                <p className="text-[10px] text-[#1E3A5F]/80 mb-2 leading-snug">
                  اختر أي المربعات الملونة (المبيعات/المستلم/الأرباح/رأس المال/...) تريد يشوفها هذا الحساب بواجهة الهوم.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                  {homeWidgets.map((w) => {
                    const on = allowed.has(w.id);
                    return (
                      <button
                        type="button"
                        key={w.id}
                        onClick={() => toggle(w.id)}
                        data-testid={`home-widget-${w.id}`}
                        className={`relative text-right rounded-xl px-2.5 py-2 text-[11px] font-bold border-2 transition-all ${
                          on
                            ? "bg-gradient-to-br from-[#DBEAFE] to-[#93C5FD] border-[#3B82F6] text-[#1E3A5F] shadow"
                            : "bg-white border-[#E4EBE6] text-[#5C6E64] hover:border-[#3B82F6]/50"
                        }`}
                      >
                        {on && <Check className="absolute top-1 left-1 w-3 h-3 text-[#1E3A5F]" strokeWidth={3} />}
                        <div className="flex items-center gap-1.5">
                          {on && <Star className="w-3 h-3 fill-[#3B82F6] text-[#3B82F6] shrink-0" />}
                          <span className="truncate">{w.label}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-[#E4EBE6] px-4 py-3 flex items-center justify-between gap-2 bg-[#F9FAFB]">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-4 py-2 text-sm font-bold text-[#5C6E64] hover:text-[#111814]"
            >
              إلغاء
            </button>
            <button
              onClick={save}
              disabled={busy}
              data-testid="sub-admin-save-btn"
              className="bg-[#15803D] hover:bg-[#126B32] disabled:opacity-60 text-white font-black px-6 py-2 rounded-full text-sm flex items-center gap-2 shadow-lg active:scale-95 transition-transform"
            >
              <Check className="w-4 h-4" />
              {busy ? "جاري الحفظ..." : (isEdit ? "حفظ التعديل" : "إنشاء الحساب")}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SubAdminsTab;
